import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { directoryKeys } from '@/shared/api/directory';
import { api } from '@/shared/api/http';
import type {
  DepartmentDto,
  IsoDateTime,
  Language,
  PagedResult,
  UserListItem,
  UserProfile,
  UserRef,
} from '@/shared/api/types';
import { uploadFile, type UploadProgress } from '@/shared/api/upload';

/*
 * Company API (backend module `identity`, route roots /api/users, /api/departments, /api/absences, /api/invitations —
 * docs/modules/identity.md, docs/modules/company.md). Query keys start with the module id `company`; users and
 * departments share the roots of the shared pickers (`directoryKeys`), so invalidating them refreshes the pickers too.
 */

/** ISO date without time (`2026-10-09`). */
export type IsoDate = string;

/** The full profile (`GET /api/users/{id}`): the shared contract plus skills and the admin flag. */
export interface CompanyProfile extends UserProfile {
  skills: string[];
  isAdmin: boolean;
}

export interface BirthdayListItem {
  id: string;
  fullName: string;
  avatarUrl: string | null;
  position: string | null;
  departmentId: string | null;
  departmentName: string | null;
  isOnline: boolean;
  /** The year is withheld from those who may not see it (the year 2000 stands in): show day and month only. */
  birthday: IsoDate;
  birthdayYearHidden?: boolean;
}

export interface NewEmployeeListItem {
  id: string;
  fullName: string;
  avatarUrl: string | null;
  position: string | null;
  departmentId: string | null;
  departmentName: string | null;
  isOnline: boolean;
  joinedOn: IsoDate;
}

export interface LetterCount {
  letter: string;
  count: number;
}

export interface OrgChartDepartment {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  head: UserRef | null;
  memberCount: number;
  totalMemberCount: number;
  memberPreview: UserRef[];
}

export type AbsenceType = 'vacation' | 'sick' | 'businessTrip' | 'other';
export const ABSENCE_TYPES: readonly AbsenceType[] = ['vacation', 'sick', 'businessTrip', 'other'];

/** Who recorded an absence: a person on the chart, or a business process (an approved vacation request). */
export type AbsenceSource = 'manual' | 'process';

export interface AbsenceDto {
  id: string;
  userId: string;
  user: UserRef | null;
  type: AbsenceType;
  from: IsoDate;
  /** Last day (inclusive). */
  to: IsoDate;
  comment: string | null;
  /** False for a process's absence unless one is an administrator (the employee cancels it through a process). */
  canEdit: boolean;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  source: AbsenceSource;
  /** The process that recorded it — shown to the employee and administrators only (otherwise null). */
  processName: string | null;
  /** SPA link of the process's document (the request), for the employee and administrators. */
  sourceUrl: string | null;
}

export interface AbsenceInput {
  type: AbsenceType;
  from: IsoDate;
  to: IsoDate;
  comment: string | null;
}

/**
 * The settings of the absence chart (`/api/absences/settings`, docs/modules/bizproc.md §11.3): with `vacationsViaProcess`
 * only administrators record vacations by hand, everybody else submits the vacation request of the feed.
 */
export interface AbsenceSettings {
  vacationsViaProcess: boolean;
}

export interface AbsenceQuery {
  from: IsoDate;
  to: IsoDate;
  departmentId?: string;
  userId?: string;
}

export interface ManagedUserItem extends UserListItem {
  isAdmin: boolean;
  hireDate: IsoDate | null;
  createdAt: IsoDateTime;
  deactivatedAt: IsoDateTime | null;
  lastSeenAt: IsoDateTime | null;
  /** The account uses two-factor authentication. */
  twoFactorEnabled?: boolean;
  /** Locked out after too many failed sign-ins (an administrator can unlock it). */
  isLocked?: boolean;
  /** Has the HR role (deactivates employees and hands their work over). */
  isHr?: boolean;
  /** Keys of the person's roles besides `admin` and `user` (`hr` included); absent in answers of an older API. */
  roles?: string[];
  /** Deactivated accounts only: how many things are still assigned to the person (null for active accounts). */
  openItems?: number | null;
}

// ---- offboarding (docs/modules/identity.md "Offboarding") -------------------------------------------------------------

/** How many things of one kind hang on a person (`category` = `<module>.<kind>`, e.g. `tasks.responsible`). */
export interface OffboardingItem {
  category: string;
  count: number;
}

export interface OffboardingModule {
  module: string;
  items: OffboardingItem[];
  total: number;
}

export interface OffboardingPreview {
  modules: OffboardingModule[];
  total: number;
}

export type OffboardingDiskTarget = 'successor' | 'company' | 'skip';
export type OffboardingRunStatus = 'running' | 'completed' | 'attention';
export type OffboardingStepStatus = 'pending' | 'completed' | 'incomplete' | 'failed';

export interface OffboardingStep {
  module: string;
  status: OffboardingStepStatus;
  attempts: number;
  planned: OffboardingItem[];
  remaining: OffboardingItem[];
  plannedTotal: number;
  remainingTotal: number;
  error: string | null;
  requestedAt: IsoDateTime;
  finishedAt: IsoDateTime | null;
}

export interface OffboardingRun {
  id: string;
  subject: UserRef | null;
  successor: UserRef | null;
  actor: UserRef | null;
  status: OffboardingRunStatus;
  deactivated: boolean;
  options: { includeClosedTasks: boolean; diskTarget: OffboardingDiskTarget; modules: string[] | null };
  createdAt: IsoDateTime;
  finishedAt: IsoDateTime | null;
  plannedTotal: number;
  movedTotal: number;
  remainingTotal: number;
  steps: OffboardingStep[];
}

export interface OffboardingStatus {
  isActive: boolean;
  open: OffboardingPreview;
  runningRunId: string | null;
  runs: OffboardingRun[];
}

export interface OffboardingOptions {
  includeClosedTasks: boolean;
  diskTarget: OffboardingDiskTarget;
  /** The modules to run; undefined — every module that holds something. */
  modules?: string[];
}

export interface StartOffboardingInput extends OffboardingOptions {
  successorId: string;
  /** Deactivate the account first (the wizard of the deactivation dialog). */
  deactivate: boolean;
}

export type ManagedUserStatus = 'active' | 'inactive' | 'all';
export type ManagedUserRole = 'all' | 'admins' | 'employees';

export interface ManagedUserQuery {
  search?: string;
  departmentId?: string;
  status?: ManagedUserStatus;
  role?: ManagedUserRole;
  /** `name`, `-lastSeenAt`… */
  sort?: string;
  page?: number;
  pageSize?: number;
}

export interface UserAdminSummary {
  active: number;
  inactive: number;
  admins: number;
  joinedLast30Days: number;
  pendingInvitations: number;
  expiredInvitations: number;
  departments: number;
}

export type InvitationStatus = 'pending' | 'expired' | 'accepted' | 'revoked';

export interface InvitationDto {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  departmentId: string | null;
  position: string | null;
  status: InvitationStatus;
  invitedBy: UserRef | null;
  createdAt: IsoDateTime;
  expiresAt: IsoDateTime;
  lastSentAt: IsoDateTime | null;
  sendCount: number;
  acceptedAt: IsoDateTime | null;
  acceptedUserId: string | null;
  revokedAt: IsoDateTime | null;
  /**
   * The invitation link — only in the responses that issue one (create, resend): the server keeps just a hash of the
   * token, so lists never show it.
   */
  link?: string;
  /** Whether the link went out by e-mail; `false` without an SMTP server (the administrator passes the link on). */
  emailSent?: boolean;
}

/** `GET /api/invitations/delivery`: e-mail, or (no SMTP server configured) links the administrator passes on. */
export interface InvitationDelivery {
  emailEnabled: boolean;
}

export interface InvitationCounts {
  pending: number;
  expired: number;
  accepted: number;
  revoked: number;
  all: number;
}

export interface InvitationQuery {
  status?: InvitationStatus | 'all';
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateInvitationInput {
  email: string;
  firstName: string | null;
  lastName: string | null;
  departmentId: string | null;
  position: string | null;
}

export interface DirectoryQuery {
  search?: string;
  departmentId?: string;
  includeSubDepartments?: boolean;
  letter?: string;
  pageSize?: number;
}

export interface UpdateProfileInput {
  firstName: string;
  lastName: string;
  phones: string[];
  birthday: IsoDate | null;
  about: string | null;
  skills: string[];
}

export interface EmploymentInput {
  departmentId: string | null;
  position: string | null;
  hireDate: IsoDate | null;
}

export interface DepartmentInput {
  name: string;
  headUserId: string | null;
  sortOrder?: number | null;
}

export interface CreateDepartmentInput extends DepartmentInput {
  parentId: string | null;
}

export const companyKeys = {
  all: ['company'] as const,
  users: directoryKeys.users,
  directory: (params: DirectoryQuery) => [...directoryKeys.users, 'directory', params] as const,
  letters: (departmentId: string | undefined) =>
    [...directoryKeys.users, 'letters', departmentId ?? null] as const,
  user: directoryKeys.user,
  birthdays: (month: number) => [...directoryKeys.users, 'birthdays', month] as const,
  newcomers: (days: number) => [...directoryKeys.users, 'new', days] as const,
  departments: directoryKeys.departments,
  chart: [...directoryKeys.departments, 'chart'] as const,
  absences: ['company', 'absences'] as const,
  absenceList: (params: AbsenceQuery) => ['company', 'absences', params] as const,
  absenceSettings: ['company', 'absence-settings'] as const,
  admin: ['company', 'admin'] as const,
  managedUsers: (params: ManagedUserQuery) => ['company', 'admin', 'users', params] as const,
  summary: ['company', 'admin', 'summary'] as const,
  offboarding: (userId: string) => ['company', 'offboarding', userId] as const,
  offboardingPreview: (userId: string, options: OffboardingOptions) =>
    ['company', 'offboarding', userId, 'preview', options] as const,
  offboardingRun: (runId: string) => ['company', 'offboarding', 'run', runId] as const,
  invitations: (params: InvitationQuery) => ['company', 'admin', 'invitations', params] as const,
  invitationCounts: ['company', 'admin', 'invitation-counts'] as const,
  invitationDelivery: ['company', 'invitation-delivery'] as const,
};

const id = (value: string) => encodeURIComponent(value);

export const companyApi = {
  directory: (params: DirectoryQuery & { page: number }, signal?: AbortSignal) =>
    api.get<PagedResult<UserListItem>>('/users', { query: { ...params }, signal }),
  letters: (departmentId: string | undefined, signal?: AbortSignal) =>
    api.get<LetterCount[]>('/users/letters', {
      query: { departmentId, includeSubDepartments: departmentId ? true : undefined },
      signal,
    }),
  user: (userId: string, signal?: AbortSignal) => api.get<CompanyProfile>(`/users/${id(userId)}`, { signal }),
  birthdays: (month: number, signal?: AbortSignal) =>
    api.get<BirthdayListItem[]>('/users/birthdays', { query: { month }, signal }),
  newcomers: (days: number, signal?: AbortSignal) =>
    api.get<NewEmployeeListItem[]>('/users/new', { query: { days }, signal }),
  updateProfile: (input: UpdateProfileInput) => api.put<CompanyProfile>('/users/me', input),
  uploadAvatar: async (file: File, onProgress?: (progress: UploadProgress) => void) =>
    (await uploadFile(file, { path: '/users/me/avatar', onProgress })) as unknown as CompanyProfile,
  removeAvatar: () => api.delete<CompanyProfile>('/users/me/avatar'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  chart: (signal?: AbortSignal) => api.get<OrgChartDepartment[]>('/departments/chart', { signal }),
  createDepartment: (input: CreateDepartmentInput) => api.post<DepartmentDto>('/departments', input),
  updateDepartment: (departmentId: string, input: DepartmentInput) =>
    api.put<DepartmentDto>(`/departments/${id(departmentId)}`, input),
  moveDepartment: (departmentId: string, parentId: string | null, sortOrder?: number | null) =>
    api.post<DepartmentDto>(`/departments/${id(departmentId)}/move`, { parentId, sortOrder }),
  deleteDepartment: (departmentId: string) => api.delete(`/departments/${id(departmentId)}`),
  absences: (params: AbsenceQuery, signal?: AbortSignal) =>
    api.get<AbsenceDto[]>('/absences', {
      query: { ...params, includeSubDepartments: params.departmentId ? true : undefined },
      signal,
    }),
  createAbsence: (input: AbsenceInput & { userId?: string | null }) =>
    api.post<AbsenceDto>('/absences', input),
  updateAbsence: (absenceId: string, input: AbsenceInput) =>
    api.put<AbsenceDto>(`/absences/${id(absenceId)}`, input),
  deleteAbsence: (absenceId: string) => api.delete(`/absences/${id(absenceId)}`),
  absenceSettings: (signal?: AbortSignal) => api.get<AbsenceSettings>('/absences/settings', { signal }),
  setAbsenceSettings: (input: AbsenceSettings) => api.put<AbsenceSettings>('/absences/settings', input),
  managedUsers: (params: ManagedUserQuery, signal?: AbortSignal) =>
    api.get<PagedResult<ManagedUserItem>>('/users/manage', { query: { ...params }, signal }),
  summary: (signal?: AbortSignal) => api.get<UserAdminSummary>('/users/manage/summary', { signal }),
  deactivate: (userId: string) => api.post(`/users/${id(userId)}/deactivate`),
  reactivate: (userId: string) => api.post(`/users/${id(userId)}/reactivate`),
  grantHr: (userId: string) => api.post(`/users/${id(userId)}/hr`),
  revokeHr: (userId: string) => api.delete(`/users/${id(userId)}/hr`),
  offboardingStatus: (userId: string, signal?: AbortSignal) =>
    api.get<OffboardingStatus>(`/users/${id(userId)}/offboarding`, { signal }),
  offboardingPreview: (userId: string, options: OffboardingOptions, signal?: AbortSignal) =>
    api.get<OffboardingPreview>(`/users/${id(userId)}/offboarding/preview`, {
      query: { includeClosedTasks: options.includeClosedTasks, diskTarget: options.diskTarget },
      signal,
    }),
  startOffboarding: (userId: string, input: StartOffboardingInput) =>
    api.post<OffboardingRun>(`/users/${id(userId)}/offboarding`, input),
  offboardingRun: (runId: string, signal?: AbortSignal) =>
    api.get<OffboardingRun>(`/offboarding/runs/${id(runId)}`, { signal }),
  retryOffboarding: (runId: string) => api.post<OffboardingRun>(`/offboarding/runs/${id(runId)}/retry`),
  grantAdmin: (userId: string) => api.post(`/users/${id(userId)}/admin`),
  revokeAdmin: (userId: string) => api.delete(`/users/${id(userId)}/admin`),
  updateEmployment: (userId: string, input: EmploymentInput) =>
    api.put<CompanyProfile>(`/users/${id(userId)}/employment`, input),
  invitations: (params: InvitationQuery, signal?: AbortSignal) =>
    api.get<PagedResult<InvitationDto>>('/invitations', { query: { ...params }, signal }),
  invitationCounts: (signal?: AbortSignal) => api.get<InvitationCounts>('/invitations/counts', { signal }),
  invitationDelivery: (signal?: AbortSignal) =>
    api.get<InvitationDelivery>('/invitations/delivery', { signal }),
  invite: (input: CreateInvitationInput) => api.post<InvitationDto>('/invitations', input),
  resendInvitation: (invitationId: string) =>
    api.post<InvitationDto>(`/invitations/${id(invitationId)}/resend`),
  revokeInvitation: (invitationId: string) => api.post(`/invitations/${id(invitationId)}/revoke`),
};

// ---- queries ------------------------------------------------------------------------------------------------------

export const DIRECTORY_PAGE_SIZE = 48;

/** The employee directory, page by page (infinite scroll). */
export function useDirectory(params: DirectoryQuery) {
  return useInfiniteQuery({
    queryKey: companyKeys.directory(params),
    queryFn: ({ pageParam, signal }) =>
      companyApi.directory({ pageSize: DIRECTORY_PAGE_SIZE, ...params, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.pageSize < last.total ? last.page + 1 : undefined),
    placeholderData: keepPreviousData,
  });
}

export function useLetters(departmentId: string | undefined) {
  return useQuery({
    queryKey: companyKeys.letters(departmentId),
    queryFn: ({ signal }) => companyApi.letters(departmentId, signal),
    staleTime: 60_000,
  });
}

/** The full profile; shares its cache entry with the shared `useUser` (same endpoint, same key). */
export function useProfile(userId: string | null | undefined) {
  return useQuery({
    queryKey: companyKeys.user(userId ?? ''),
    queryFn: ({ signal }) => companyApi.user(userId ?? '', signal),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useBirthdays(month: number) {
  return useQuery({
    queryKey: companyKeys.birthdays(month),
    queryFn: ({ signal }) => companyApi.birthdays(month, signal),
    staleTime: 5 * 60_000,
  });
}

export function useNewcomers(days = 30) {
  return useQuery({
    queryKey: companyKeys.newcomers(days),
    queryFn: ({ signal }) => companyApi.newcomers(days, signal),
    staleTime: 5 * 60_000,
  });
}

export function useOrgChart() {
  return useQuery({
    queryKey: companyKeys.chart,
    queryFn: ({ signal }) => companyApi.chart(signal),
    staleTime: 60_000,
  });
}

/** Whether vacations are recorded only through the vacation request (everybody reads it: the dialog shows the hint). */
export function useAbsenceSettings() {
  return useQuery({
    queryKey: companyKeys.absenceSettings,
    queryFn: ({ signal }) => companyApi.absenceSettings(signal),
    staleTime: 60_000,
  });
}

/** Turns «vacations only through a process» on or off (administrators). */
export function useSetAbsenceSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: companyApi.setAbsenceSettings,
    onSuccess: (settings) => queryClient.setQueryData(companyKeys.absenceSettings, settings),
  });
}

export function useAbsences(params: AbsenceQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: companyKeys.absenceList(params),
    queryFn: ({ signal }) => companyApi.absences(params, signal),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}

export function useManagedUsers(params: ManagedUserQuery) {
  return useQuery({
    queryKey: companyKeys.managedUsers(params),
    queryFn: ({ signal }) => companyApi.managedUsers(params, signal),
    placeholderData: keepPreviousData,
  });
}

/** What is assigned to a person now, the running handover and the latest ones (administrators and HR). */
export function useOffboardingStatus(userId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: companyKeys.offboarding(userId),
    queryFn: ({ signal }) => companyApi.offboardingStatus(userId, signal),
    enabled: options.enabled ?? true,
    // A running handover changes the numbers: look again until it is over.
    refetchInterval: (query) => (query.state.data?.runningRunId ? 2_000 : false),
  });
}

/** What a handover with these options would move, per module. */
export function useOffboardingPreview(
  userId: string,
  options: OffboardingOptions,
  queryOptions: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: companyKeys.offboardingPreview(userId, options),
    queryFn: ({ signal }) => companyApi.offboardingPreview(userId, options, signal),
    placeholderData: keepPreviousData,
    enabled: queryOptions.enabled ?? true,
  });
}

/** A handover with the report of every module; polls while it runs. */
export function useOffboardingRun(runId: string | null) {
  return useQuery({
    queryKey: companyKeys.offboardingRun(runId ?? ''),
    queryFn: ({ signal }) => companyApi.offboardingRun(runId ?? '', signal),
    enabled: !!runId,
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 1_500 : false),
  });
}

export function useAdminSummary(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: companyKeys.summary,
    queryFn: ({ signal }) => companyApi.summary(signal),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useInvitations(params: InvitationQuery) {
  return useQuery({
    queryKey: companyKeys.invitations(params),
    queryFn: ({ signal }) => companyApi.invitations(params, signal),
    placeholderData: keepPreviousData,
  });
}

export function useInvitationCounts(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: companyKeys.invitationCounts,
    queryFn: ({ signal }) => companyApi.invitationCounts(signal),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

/** How invitations reach people (configuration: changes only with a restart of the server). */
export function useInvitationDelivery(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: companyKeys.invitationDelivery,
    queryFn: ({ signal }) => companyApi.invitationDelivery(signal),
    staleTime: Infinity,
    enabled: options.enabled ?? true,
  });
}

// ---- cache helpers -------------------------------------------------------------------------------------------------

/** Everything derived from people: directory, profiles, widgets, pickers, org chart, administration. */
export function invalidatePeople(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['company', 'offboarding'] });
  void queryClient.invalidateQueries({ queryKey: companyKeys.users });
  void queryClient.invalidateQueries({ queryKey: companyKeys.departments });
  void queryClient.invalidateQueries({ queryKey: companyKeys.admin });
}

/** Org structure: departments (pickers, chart) and the directory rows that show department names. */
export function invalidateOrg(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: companyKeys.departments });
  void queryClient.invalidateQueries({ queryKey: companyKeys.users });
  void queryClient.invalidateQueries({ queryKey: companyKeys.admin });
}

export function invalidateAbsences(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: companyKeys.absences });
}

/** Stores a fresh own profile and refreshes what shows it. */
export function useProfileCacheUpdate() {
  const queryClient = useQueryClient();
  return (profile: CompanyProfile) => {
    queryClient.setQueryData(companyKeys.user(profile.id), profile);
    void queryClient.invalidateQueries({ queryKey: companyKeys.users, refetchType: 'active' });
  };
}

export function useAbsenceMutations() {
  const queryClient = useQueryClient();
  const done = () => invalidateAbsences(queryClient);
  return {
    create: useMutation({
      mutationFn: companyApi.createAbsence,
      onSuccess: done,
      meta: { silent: true },
    }),
    update: useMutation({
      mutationFn: ({ absenceId, input }: { absenceId: string; input: AbsenceInput }) =>
        companyApi.updateAbsence(absenceId, input),
      onSuccess: done,
      meta: { silent: true },
    }),
    remove: useMutation({
      mutationFn: companyApi.deleteAbsence,
      onSuccess: done,
    }),
  };
}

/** Preference values of the profile form (time zone / language are account preferences, not profile fields). */
export interface PreferencesInput {
  locale: Language;
  timeZone: string;
}
